import React, { useState } from "react";
import { Folder, FolderPlus, Layers, Inbox, Check, X, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const SWATCHES = ["#f59e0b", "#22c55e", "#3b82f6", "#ec4899", "#a855f7", "#ef4444", "#14b8a6", "#eab308"];
const PROVIDERS = [{ v: "sparkpost", l: "SparkPost" }, { v: "infobip", l: "Infobip" }];

/**
 * Left rail on the Sites list — pick "All sites", a folder, or "Unfiled" to
 * filter the table. Create / rename / delete folders inline.
 */
export default function FolderRail({ folders, unfiledCount, totalSites, active, onSelect, reload }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(SWATCHES[0]);
  const [newProvider, setNewProvider] = useState("sparkpost");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editProvider, setEditProvider] = useState("sparkpost");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function create() {
    if (!newName.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post("/folders", { name: newName.trim(), color: newColor, email_provider: newProvider });
      setNewName(""); setCreating(false); setNewColor(SWATCHES[0]); setNewProvider("sparkpost");
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  }

  async function rename(id) {
    if (!editName.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.patch(`/folders/${id}`, { name: editName.trim(), email_provider: editProvider });
      setEditingId(null); await reload();
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  }

  async function remove(id, name, count) {
    const msg = count
      ? `Delete folder "${name}"? Its ${count} site${count === 1 ? "" : "s"} will be unfiled (not deleted).`
      : `Delete folder "${name}"?`;
    if (!window.confirm(msg)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/folders/${id}`);
      if (active === id) onSelect("all");
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  }

  const RailBtn = ({ id, icon: Icon, label, count, dot, badge }) => (
    <button
      data-testid={`folder-rail-${id}`}
      onClick={() => onSelect(id)}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition ${
        active === id ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60"
      }`}
    >
      {dot ? <span className="dot shrink-0" style={{ background: dot }} /> : <Icon size={15} className="shrink-0" />}
      <span className="truncate flex-1 text-left">{label}</span>
      {badge && <span className="text-[9px] px-1 rounded-sm bg-sky-500/20 text-sky-300 mono shrink-0">{badge}</span>}
      <span className="mono text-[11px] text-zinc-500">{count}</span>
    </button>
  );

  return (
    <aside data-testid="folder-rail" className="w-56 shrink-0 border-r border-zinc-800 pr-4 space-y-1">
      <div className="label-tiny px-3 pb-1">Folders</div>
      <RailBtn id="all" icon={Layers} label="All sites" count={totalSites} />
      {folders.map((f) => (
        <div key={f.id} className="group relative">
          {editingId === f.id ? (
            <div className="px-2 py-1 space-y-1">
              <div className="flex items-center gap-1">
                <input
                  autoFocus
                  data-testid={`folder-rename-input-${f.id}`}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && rename(f.id)}
                  className="flex-1 min-w-0 bg-transparent border border-zinc-700 rounded-sm px-2 py-1 text-sm"
                />
                <button data-testid={`folder-rename-save-${f.id}`} onClick={() => rename(f.id)} className="text-emerald-400 p-1"><Check size={14} /></button>
                <button onClick={() => setEditingId(null)} className="text-zinc-500 p-1"><X size={14} /></button>
              </div>
              <select
                data-testid={`folder-edit-provider-${f.id}`}
                value={editProvider}
                onChange={(e) => setEditProvider(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1 text-xs"
              >
                {PROVIDERS.map((p) => <option key={p.v} value={p.v}>Email: {p.l}</option>)}
              </select>
            </div>
          ) : (
            <div className="flex items-center">
              <RailBtn id={f.id} label={f.name} count={f.site_count} dot={f.color} badge={f.email_provider === "infobip" ? "IB" : null} />
              <div className="absolute right-1 hidden group-hover:flex items-center gap-0.5 bg-zinc-900/90 rounded-sm">
                <button
                  data-testid={`folder-edit-${f.id}`}
                  onClick={() => { setEditingId(f.id); setEditName(f.name); setEditProvider(f.email_provider || "sparkpost"); }}
                  className="text-zinc-400 hover:text-white p-1"
                ><Pencil size={12} /></button>
                <button
                  data-testid={`folder-delete-${f.id}`}
                  onClick={() => remove(f.id, f.name, f.site_count)}
                  className="text-zinc-400 hover:text-rose-400 p-1"
                ><Trash2 size={12} /></button>
              </div>
            </div>
          )}
        </div>
      ))}
      <RailBtn id="unfiled" icon={Inbox} label="Unfiled" count={unfiledCount} />

      {creating ? (
        <div className="px-2 pt-2 space-y-2">
          <input
            autoFocus
            data-testid="folder-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
            placeholder="Folder name (e.g. Server 1)"
            className="w-full bg-transparent border border-zinc-700 rounded-sm px-2 py-1.5 text-sm"
          />
          <div className="flex items-center gap-1.5 flex-wrap">
            {SWATCHES.map((c) => (
              <button
                key={c}
                onClick={() => setNewColor(c)}
                className={`w-5 h-5 rounded-full border ${newColor === c ? "ring-2 ring-white/60 border-transparent" : "border-zinc-700"}`}
                style={{ background: c }}
              />
            ))}
          </div>
          <select
            data-testid="folder-new-provider"
            value={newProvider}
            onChange={(e) => setNewProvider(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1.5 text-xs"
          >
            {PROVIDERS.map((p) => <option key={p.v} value={p.v}>Email provider: {p.l}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <button
              data-testid="folder-create-btn"
              onClick={create}
              disabled={busy || !newName.trim()}
              className="flex-1 bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 text-xs font-semibold uppercase tracking-wider px-2 py-1.5 rounded-sm disabled:opacity-40"
            >Create</button>
            <button onClick={() => { setCreating(false); setNewName(""); }} className="text-zinc-500 hover:text-zinc-300 text-xs px-2 py-1.5">Cancel</button>
          </div>
        </div>
      ) : (
        <button
          data-testid="folder-new-toggle"
          onClick={() => setCreating(true)}
          className="w-full flex items-center gap-2 px-3 py-2 mt-2 rounded-sm text-sm text-zinc-400 hover:text-white hover:bg-zinc-900/60 transition border border-dashed border-zinc-800"
        >
          <FolderPlus size={15} /> New folder
        </button>
      )}
      {err && <div data-testid="folder-rail-error" className="text-[11px] text-rose-400 px-3 pt-1">{err}</div>}
    </aside>
  );
}
